/* eslint-disable no-nested-ternary */
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { FC } from 'react';
import { Box, Checkbox, TableRow } from '@mui/material';
import { StyledTableCell } from 'ui/StyledTable';
import { nanoid } from '@reduxjs/toolkit';
import { useLocation, useNavigate } from 'react-router';
import queryString from 'query-string';
import { ETableStructureType, TTableStructure } from 'services/constants/tableStructureTypes';
import { TFilters, ESortDirection } from 'services/constants/types';
import { DATES_ARRAY } from 'services/constants/dates';
import { ActionSpeedDial } from './ActionSpeedDial';
import { ECheckedAll } from '../types';

export type TEasTableHeaderProps<T> = {
	tableStructure: TTableStructure<T>[];
	checkedAll?: ECheckedAll;
	setCheckAll?: (checked: ECheckedAll) => void;
};

export const EasTableHeader = <T,>({
	tableStructure,
	checkedAll = ECheckedAll.NO_ONE,
	setCheckAll,
}: TEasTableHeaderProps<T>) => {
	const navigate = useNavigate();
	const location = useLocation();
	const { search } = useLocation();
	const navigateParams = queryString.parse(search);

	const filtersArray: TFilters = tableStructure.reduce(
		(acc, item) => {
			if (navigateParams[item.selector as keyof typeof navigateParams])
				acc.push({
					fieldName: item.selector as string,
					value: navigateParams[item.selector as string] as string,
				});
			return acc;
		},
		[] as { fieldName: string; value: string }[],
	);

	const filterMatrix = tableStructure?.map((item, index) => {
		if (navigateParams[`filter${index}`]) return navigateParams[`filter${index}`] as string;
		return '';
	});

	const handleSort = (index: number, direction: ESortDirection) => {
		const newFilters = [...filtersArray];

		navigate(
			`${location.pathname}?${queryString.stringify({
				...navigateParams,
				sortIndex: index,
				sortDirection: direction,
				page: 1,
			})}`,
		);

		const timestampIndex = newFilters.findIndex?.((item) => item?.fieldName === 'timestamp');
		if (timestampIndex > -1) {
			const dates = DATES_ARRAY.find((item) => item.id === newFilters?.[timestampIndex]?.value);
			if (dates?.from) {
				newFilters.splice(timestampIndex, 1);
				newFilters.push({
					fieldName: 'timestamp',
					value: dates.from,
					operator: '>=',
				});
				if (dates?.to) {
					newFilters.push({
						fieldName: 'timestamp',
						value: dates.to,
						operator: '<=',
					});
				}
			}
		}

		const schemesIndex = newFilters.findIndex?.((item) => item?.fieldName === 'schemes');
		if (schemesIndex > -1) {
			const schemesValue = newFilters?.[schemesIndex]?.value as string;
			newFilters.splice(schemesIndex, 1);
			newFilters.push({
				fieldName: 'schemes',
				value: `%${schemesValue}%`,
				operator: 'like',
			});
		}

		const idIndex = newFilters.findIndex?.((item) => item?.fieldName === 'external_order_id');
		if (idIndex > -1) {
			const idValue = newFilters?.[idIndex]?.value as string;
			newFilters.splice(idIndex, 1);
			newFilters.push({
				fieldName: 'external_order_id',
				value: `%${idValue}%`,
				operator: 'like',
			});
		}

		// dispatch(
		// 	getConfirmedOrdersRequest({
		// 		filter: newFilters,
		// 		orderBy: direction
		// 			? [`${tableStructure[index].selector} ${direction as string}`]
		// 			: ['id desc'],
		// 		page: {
		// 			from: 0,
		// 			size: PAGE_SIZE,
		// 		},
		// 		tableName: 'dashboard_confirmed_orders',
		// 	}),
		// );
	};

	const sort =
		navigateParams.sortIndex && navigateParams.sortDirection
			? {
					index: Number(navigateParams.sortIndex ?? 0),
					direction: (navigateParams.sortDirection ?? ESortDirection.ASC) as ESortDirection,
				}
			: null;

	return (
		<TableRow>
			{tableStructure?.map((filter, index: number) => {
				if (filter.show === false) return null;

				if (filter?.noName) return <StyledTableCell key={nanoid()} />;
				return (
					<StyledTableCell
						key={nanoid()}
						align={filter?.align ?? 'center'}
						colSpan={filter.colSpan}
						style={{
							padding: '0px 5px 0px 5px',
							verticalAlign: 'top',
							backgroundColor:
								sort?.index === index
									? '#004468'
									: filterMatrix?.[index]
										? '#015079'
										: // : filter.selector === 'timestamp' && (dateRange.from || dateRange.to)
											// ? '#015079'
											'#006293',
						}}
					>
						<Box
							sx={{
								display: 'inline-flex',
								alignItems: 'start',
								justifyContent: 'start',
								flexDirection: 'row',
							}}
						>
							{filter.type === ETableStructureType.CHECKBOX ? (
								<Checkbox
									checked={checkedAll === ECheckedAll.ALL}
									indeterminate={checkedAll === ECheckedAll.SOME}
									sx={{
										color: 'white',
										m: 0,
										p: 0,
										pl: 1,
										'&.Mui-checked': {
											color: '#ffffff',
										},
										'&.Mui-indeterminate': {
											color: '#ffffff',
										},
										'& .MuiSvgIcon-root': {
											color: '#ffffff',
										},
										'&.Mui-indeterminate .MuiSvgIcon-root': {
											color: '#ffffff',
											opacity: 1,
										},
									}}
									onChange={(e) => {
										e.stopPropagation();
										setCheckAll?.(e.target.checked ? ECheckedAll.ALL : ECheckedAll.NO_ONE);
									}}
									onClick={(e) => {
										e.stopPropagation();
									}}
								/>
							) : (
								<Box
									sx={{
										display: 'block',
										m: '4px 0 4px 0',
										lineHeight: '1.4',
										textAlign: 'left',
										fontSize: '0.75rem',
									}}
								>
									{filter.name}
								</Box>
							)}
							{filter.sortable && (
								<Box
									sx={{
										display: 'inline-flex',
										alignItems: 'center',
										justifyContent: 'center',
										flexDirection: 'column',
										ml: '2px',
									}}
								>
									<KeyboardArrowUpIcon
										sx={{
											display: 'inline',
											mb: '-5px',
											mt: '0px',

											fontSize: 18,
											opacity:
												sort?.index === index && sort?.direction === ESortDirection.ASC ? 1 : 0.3,
										}}
										fontSize="small"
									/>
									<KeyboardArrowDownIcon
										sx={{
											display: 'inline',
											mt: '-5px',
											fontSize: 18,
											opacity:
												sort?.index === index && sort?.direction === ESortDirection.DESC ? 1 : 0.3,
										}}
										fontSize="small"
									/>
									<ActionSpeedDial
										sortable={filter.sortable}
										onChangeSort={(direction: ESortDirection) => handleSort(index, direction)}
										sortDirection={sort?.index === index ? sort?.direction : undefined}
									/>
								</Box>
							)}
						</Box>
					</StyledTableCell>
				);
			})}
		</TableRow>
	);
};
